
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Navigation,
  MessageCircle,
  Send,
  HeartPulse,
  CheckCircle2,
  ArrowRight,
  Hospital,
} from "lucide-react";

const hospital = {
  name: "Pro Cesta Physiotherapy Hospital",
  tagline: "Advanced Physiotherapy & Rehabilitation Care",
  address:
    "House No. 117, Sugandha Residential Gate (Beside Ekushey Hospital), Mirzarpool, Panchlaish, Chattogram, Bangladesh",
  phones: ["01842976655", "01886373893"],
  email: "info@procestaph.com",
  secondaryEmail: "procestaph@gmail.com",
  mapQuery:
    "Pro Cesta Physiotherapy Hospital, Sugandha Residential Area, Panchlaish, Chattogram",
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

function ContactInfoCard({ icon: Icon, title, children, color = "blue" }) {
  return (
    <motion.div
      variants={fadeUp}
      className="group flex gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 transition-colors group-hover:bg-sky-600 group-hover:text-white">
        <Icon size={22} />
      </div>

      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-500">{title}</h3>
        <div className="mt-1 text-sm leading-6 text-slate-800">
          {children}
        </div>
      </div>
    </motion.div>
  );
}

function HotlineButton({ phone }) {
  return (
    <a
      href={`tel:${phone}`}
      className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-sky-700 shadow-sm transition hover:bg-sky-50"
    >
      <Phone size={16} />
      {phone}
    </a>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.message.trim()
    ) {
      setStatus("error");
      return;
    }

    setStatus("sending");

    try {
      // Replace this with your actual API request.
      await new Promise((resolve) => setTimeout(resolve, 900));

      setStatus("success");

      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  }

  return (
    <main className="min-h-screen overflow-hidden bg-slate-50 text-slate-800">
      {/* =====================================================
          HERO
      ====================================================== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-700 via-sky-600 to-cyan-500 text-white">
        {/* Decorative circles */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border-[35px] border-white/10" />
        <div className="pointer-events-none absolute -bottom-40 -left-20 h-96 w-96 rounded-full border-[40px] border-white/10" />

        <motion.div
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-none absolute right-[15%] top-20 h-3 w-3 rounded-full bg-cyan-200"
        />

        <div className="relative mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 shadow-lg ring-1 ring-white/20 backdrop-blur"
            >
              <Hospital size={32} />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-sky-100"
            >
              Pro Cesta Physiotherapy Hospital
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.7 }}
              className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl"
            >
              Contact Us
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mx-auto mt-5 max-w-2xl text-base leading-7 text-sky-50 sm:text-lg"
            >
              Connect with our physiotherapy team for professional
              rehabilitation care, appointments, and general enquiries.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="mt-8 flex flex-wrap items-center justify-center gap-3"
            >
              <a
                href="#contact-form"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-sky-700 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
              >
                Send an Enquiry
                <ArrowRight size={18} />
              </a>

              <a
                href={`tel:${hospital.phones[0]}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-6 py-3 font-bold text-white backdrop-blur transition hover:bg-white/20"
              >
                <Phone size={18} />
                Call Us
              </a>
            </motion.div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative -mb-1 h-12">
          <svg
            className="absolute bottom-0 h-full w-full"
            viewBox="0 0 1440 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,65 C320,120 450,0 750,35 C1050,70 1200,105 1440,35 L1440,100 L0,100 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* =====================================================
          24/7 HOTLINE BANNER
      ====================================================== */}
      <section className="relative z-10 mx-auto -mt-1 max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-3xl bg-gradient-to-r from-sky-600 to-cyan-500 p-6 text-white shadow-xl sm:p-8"
        >
          <div className="flex flex-col items-center justify-between gap-6 lg:flex-row">
            <div className="flex items-center gap-4 text-center sm:text-left">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/15">
                <Clock size={28} />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-sky-100">
                  Available for assistance
                </p>
                <h2 className="mt-1 text-2xl font-black sm:text-3xl">
                  24-Hour Hotline
                </h2>
                <p className="mt-1 text-sm text-sky-100">
                  Contact us for assistance and enquiries.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              {hospital.phones.map((phone) => (
                <HotlineButton key={phone} phone={phone} />
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* =====================================================
          CONTACT DETAILS
      ====================================================== */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={stagger}
          className="grid gap-4 md:grid-cols-2"
        >
          <ContactInfoCard icon={MapPin} title="Hospital Address">
            <p>{hospital.address}</p>
          </ContactInfoCard>

          <ContactInfoCard icon={Phone} title="Hotline Numbers">
            <div className="flex flex-col gap-1">
              {hospital.phones.map((phone) => (
                <a
                  key={phone}
                  href={`tel:${phone}`}
                  className="font-semibold text-sky-700 hover:underline"
                >
                  {phone}
                </a>
              ))}
            </div>
          </ContactInfoCard>

          <ContactInfoCard icon={Mail} title="Email Address">
            <div className="flex flex-col gap-1 break-all">
              <a
                href={`mailto:${hospital.email}`}
                className="hover:text-sky-600"
              >
                {hospital.email}
              </a>
              <a
                href={`mailto:${hospital.secondaryEmail}`}
                className="text-slate-500 hover:text-sky-600"
              >
                {hospital.secondaryEmail}
              </a>
            </div>
          </ContactInfoCard>

          <ContactInfoCard icon={Clock} title="Service Availability">
            <p className="font-semibold text-sky-700">
              24 Hours Open
            </p>
            <p className="text-slate-500">
              Contact our hospital for service information.
            </p>
          </ContactInfoCard>
        </motion.div>
      </section>

      {/* =====================================================
          LOCATION + CONTACT FORM
      ====================================================== */}
      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
        <div className="grid items-start gap-8 lg:grid-cols-5">
          {/* Location */}
          <motion.div
            initial={{ opacity: 0, x: -25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-lg lg:col-span-3"
          >
            <div className="p-6 sm:p-8">
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-bold uppercase tracking-widest text-sky-600">
                    Visit Our Hospital
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-900 sm:text-3xl">
                    Find Our Location
                  </h2>
                </div>

                <div className="hidden rounded-xl bg-sky-50 p-3 text-sky-600 sm:block">
                  <MapPin size={24} />
                </div>
              </div>

              <p className="text-sm leading-6 text-slate-500">
                {hospital.address}
              </p>
            </div>

            <div className="h-80 w-full overflow-hidden sm:h-96">
              <iframe
                title="Pro Cesta Physiotherapy Hospital Location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(hospital.mapQuery)}&output=embed`}
                className="h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 p-6 sm:p-8">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin size={16} className="text-sky-600" />
                <span>Panchlaish, Chattogram</span>
              </div>

              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hospital.mapQuery)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-sky-700"
              >
                <Navigation size={16} />
                Get Directions
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            id="contact-form"
            initial={{ opacity: 0, x: 25 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl bg-white p-6 shadow-lg ring-1 ring-slate-100 sm:p-8 lg:col-span-2"
          >
            <div className="mb-6">
              <p className="text-sm font-bold uppercase tracking-widest text-sky-600">
                Send an Enquiry
              </p>
              <h2 className="mt-2 text-2xl font-black text-slate-900">
                Get in Touch
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Fill out the form below and our team can contact you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="01XXXXXXXXX"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Subject
                </label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
                >
                  <option value="">Select an enquiry type</option>
                  <option value="Appointment">Appointment</option>
                  <option value="Physiotherapy Service">
                    Physiotherapy Service
                  </option>
                  <option value="General Enquiry">General Enquiry</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  rows={4}
                  required
                  className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-100"
                />
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-3.5 font-bold text-white shadow-md transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send size={18} />
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>

              {status === "success" && (
                <div className="flex items-center gap-2 rounded-xl bg-green-50 p-3 text-sm text-green-700">
                  <CheckCircle2 size={18} />
                  Message sent successfully!
                </div>
              )}

              {status === "error" && (
                <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
                  Please fill in all required fields.
                </div>
              )}
            </form>

            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="text-center text-xs leading-5 text-slate-400">
                For urgent assistance, please contact our hotline directly.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* =====================================================
          QUICK CONTACT CTA
      ====================================================== */}
      <section className="bg-gradient-to-r from-sky-700 to-cyan-600 px-6 py-16 text-white">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <HeartPulse className="mx-auto mb-4" size={36} />
            <h2 className="text-3xl font-black sm:text-4xl">
              Need Physiotherapy Care?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-sky-100 sm:text-base">
              Our team is ready to assist you with information about
              physiotherapy and rehabilitation services.
            </p>

            <div className="mt-7 flex flex-wrap justify-center gap-3">
              {hospital.phones.map((phone) => (
                <HotlineButton key={phone} phone={phone} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}